<script setup lang="ts">
import { computed, defineProps, defineEmits, capitalize } from 'vue';

import { UserResponseDto } from '@clearbanc/baus-sdk';
import { useUserManagement } from '@/composables/user-management';
import { formatAddress } from '@/utils/address';
import i18n from '@/plugins/i18n';

import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';

import TeamUserProfileStatusTag from '@/pages/team/components/TeamUserProfileStatusTag.vue';
import TeamUserActionsMenu from '@/pages/team/components/TeamUserActionsMenu.vue';
import TeamResendInviteButton from '@/pages/team/components/TeamResendInviteButton.vue';
import TeamUserInviteButton from '@/pages/team/components/TeamUserInviteButton.vue';

// -------------
// Props & Emits
// -------------

const props = defineProps<{
  visible: boolean;
  user?: UserResponseDto;
}>();

const emit = defineEmits<{
  hide: [];
  deleteUser: [value: UserResponseDto];
  editUser: [value: UserResponseDto];
}>();

// -------------------
// Computed Properties
// -------------------

const userInitial = computed(() => {
  return props.user?.firstName?.charAt(0)?.toUpperCase() || '?';
});

const currentUserId = computed(() => props.user?.id);

const formattedJobRoles = computed(() => {
  if (props.user?.jobRoles && props.user.jobRoles.length > 0) {
    return (
      props.user?.jobRoles.map((role) => capitalize(role)).join(', ') ?? '-'
    );
  }
  return '-';
});

// -------
// Methods
// -------

const editProfile = (user: UserResponseDto) => {
  emit('editUser', user);
};

const handleDeleteUser = (user: UserResponseDto) => {
  emit('deleteUser', user);
};

// -----
// Setup
// -----

const { isAuthenticatedUser, canEditUser } = useUserManagement(currentUserId);
</script>

<template>
  <div class="sidebar-container">
    <Sidebar
      :visible="props.visible"
      @update:visible="emit('hide')"
      position="right"
      style="width: 30rem"
      class="team-sidebar"
    >
      <template #header>
        <span class="sidebar-title">Personal Information</span>
        <span class="ellipsis-menu" v-if="props.user">
          <TeamUserActionsMenu
            :user="props.user"
            orientation="vertical"
            @deleteUser="handleDeleteUser"
          />
        </span>
      </template>

      <template v-if="props.user">
        <div class="user-info-section">
          <div class="user-details">
            <Avatar
              :label="userInitial"
              class="user-avatar"
              size="large"
              shape="circle"
              style="background-color: #ede9fe; color: #7c3aed"
            />
            <div class="user-name-email">
              <div class="user-fullname">{{ props.user.fullName }}</div>
              <div class="user-email">{{ props.user.email }}</div>
            </div>
            <div class="user-actions">
              <div class="user-invite-actions">
                <TeamResendInviteButton :user="props.user" />
                <TeamUserInviteButton :user="props.user" label="Invite User" />
              </div>
              <Button
                icon="pi pi-pencil"
                text
                rounded
                aria-label="Edit Personal Information"
                @click="editProfile(props.user)"
                class="edit-button"
                :disabled="!canEditUser"
                v-tooltip.left.hover="
                  !canEditUser
                    ? i18n.t('data.errorMessages.cannotUpdateUserDetails')
                    : undefined
                "
              ></Button>
            </div>
          </div>
        </div>

        <div class="details-list-section">
          <div class="detail-row">
            <span class="detail-label">Profile Status</span>
            <TeamUserProfileStatusTag :userId="props.user.id" />
          </div>

          <div class="detail-row roles-row">
            <span class="detail-label">Job Roles</span>
            <span class="detail-value roles-value">
              {{ formattedJobRoles }}
            </span>
          </div>

          <div class="detail-row">
            <span class="detail-label">Job Title</span>
            <span class="detail-value">
              {{ props.user.jobTitle || '-' }}
            </span>
          </div>

          <template v-if="isAuthenticatedUser">
            <div class="detail-row">
              <span class="detail-label">Citizenship</span>
              <span class="detail-value">
                {{ props.user.citizenship || '-' }}
              </span>
            </div>

            <div class="detail-row">
              <span class="detail-label">SSN Number</span>
              <span class="detail-value">
                {{ props.user.personalTaxId || '-' }}
              </span>
            </div>

            <div class="detail-row">
              <span class="detail-label">Home Address</span>
              <span
                class="detail-value"
                v-html="formatAddress(props.user.address)"
              >
              </span>
            </div>

            <div class="detail-row">
              <span class="detail-label">Phone Number</span>
              <span class="detail-value">
                {{ props.user.phoneNumber || '-' }}
              </span>
            </div>
          </template>
        </div>
      </template>
      <template v-else>
        <div class="no-user-selected">User data not available.</div>
      </template>
    </Sidebar>
  </div>
</template>

<style lang="less" scoped>
.sidebar-title {
  font-weight: 500;
  font-size: 1.25rem;
  text-align: center;
  flex-grow: 1;
}

.user-info-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #dee2e6;
}

.user-details {
  display: flex;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
}

.user-actions {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 20px;
}

.user-invite-actions {
  flex-shrink: 0;
}

.user-invite-actions-tooltip {
  display: inline-block;
}

.resend-invite-button {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}

.user-avatar {
  margin-right: 0.75rem;
}

.user-name-email {
  max-width: 50%;
}

.user-fullname {
  font-weight: 500;
  font-size: 1.125rem;
  color: #343a40;
  margin-bottom: 0.125rem;
  word-break: break-all;
}

.user-email {
  font-size: 0.875rem;
  color: #6c757d;
  word-break: break-all;
}

.details-list-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #dee2e6;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 1.5rem;
}

.detail-label {
  color: #6c757d;
  font-size: 0.875rem;
  margin-right: 1rem;
}

.detail-value {
  font-weight: 500;
  font-size: 0.875rem;
  color: #343a40;
  text-align: right;
}

.no-user-selected {
  padding: 1rem 1.5rem;
  text-align: center;
  color: #6c757d;
}

.edit-button {
  background: transparent !important;
  border-color: transparent !important;
  color: #6c757d !important;
  padding: 0.5rem !important;
}

.edit-button .pi {
  font-size: 1.2rem;
}

.edit-button:hover {
  background: rgb(0 0 0 / 4%) !important;
}

.edit-button:focus {
  outline: none !important;
  box-shadow: none !important;
}

.edit-button:disabled {
  pointer-events: unset !important;
}

// 30rem is the width of the sidebar
@media (min-width: 30rem) {
  .user-actions {
    width: auto;
    margin-top: 0;
    margin-left: auto;
    justify-content: space-between;
  }
}
</style>

<style>
.team-sidebar .p-sidebar-header {
  justify-content: space-between;
  flex-direction: row-reverse;
  align-items: center;
}

.team-sidebar .p-sidebar-content {
  padding: 0 20px !important;
}

.team-sidebar .p-sidebar-header-content {
  display: flex;
  align-items: center;
  width: 100%;
}
</style>
